<script setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useMarketOverviewGeneration } from '@/composables/useMarketOverviewGeneration.js'

// RED-317 §8 — heads the Turuülevaated tab: the last universal-mall
// generation run (what it found, what it seeded, which universal malls are
// shown and why not, Gate A/B/C per source mall) and the one button that runs
// it again. Every string of the report was scrubbed of server paths by the
// server; nothing here adds detail of its own.
const props = defineProps({
  purposeLabels: { type: Object, required: true },
})

const { t } = useI18n()
const { run, busy, buttonLabel, error, generate } = useMarketOverviewGeneration()

const STATS = ['malls_found', 'regional_malls', 'cells_seeded', 'versions_created', 'universal_malls', 'universal_visible']

const formatDateTime = iso =>
  iso
    ? new Date(iso).toLocaleString('et-EE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : ''

const lastRunText = computed(() => {
  if (!run.value) {
    return ''
  }
  return t('messages.admin.market_overviews.generation.last_run', {
    date: formatDateTime(run.value.finished_at ?? run.value.started_at ?? run.value.created_at),
    status: t(`messages.admin.market_overviews.generation.run_status.${run.value.status}`),
  })
})

const malls = computed(() => run.value?.malls ?? [])
const universal = computed(() => run.value?.universal ?? [])
const candidates = computed(() => run.value?.candidates ?? [])

const hasProblems = computed(() =>
  malls.value.some(mall => [mall.gate_a, mall.gate_b, mall.gate_c].includes('failed')),
)
const mallsOpen = ref(false)
// A run with a failed gate opens the source list, so the reason is in sight.
watch(
  () => run.value?.id,
  () => {
    mallsOpen.value = hasProblems.value
  },
  { immediate: true },
)

const cellLabel = mall =>
  mall.status === 'regional'
    ? [mall.county, props.purposeLabels[mall.purpose] ?? mall.purpose].filter(Boolean).join(' · ')
    : t('messages.admin.market_overviews.generation.malls.skipped')

const gateSeverity = verdict => ({ passed: 'success', verified: 'success', pending: 'warn', failed: 'danger' })[verdict] ?? 'secondary'

const universalTag = row => {
  if (row.template_id === null && row.action === 'failed') {
    return { severity: 'danger', label: t('messages.admin.market_overviews.generation.universal.failed') }
  }
  if (row.admin_disabled) {
    return { severity: 'secondary', label: t('messages.admin.market_overviews.generation.universal.admin_disabled') }
  }
  return row.visible
    ? { severity: 'success', label: t('messages.admin.market_overviews.generation.universal.visible') }
    : { severity: 'warn', label: t('messages.admin.market_overviews.generation.universal.hidden') }
}
</script>

<template>
  <section class="rounded-xl border border-gray-200 bg-white p-5 shadow-xs" data-testid="mo-generation-panel">
    <h2 class="text-xl font-medium text-gray-800">{{ $t('messages.admin.market_overviews.generation.title') }}</h2>
    <p class="mt-1 max-w-3xl text-sm text-gray-500">{{ $t('messages.admin.market_overviews.generation.intro') }}</p>

    <p v-if="!run" class="mt-4 text-sm text-gray-500" data-testid="mo-generation-none">
      {{ $t('messages.admin.market_overviews.generation.none') }}
    </p>

    <template v-else>
      <p class="mt-4 text-sm text-gray-700" data-testid="mo-generation-last-run">{{ lastRunText }}</p>

      <Message v-if="run.error" severity="error" size="small" class="mt-3" data-testid="mo-generation-error">
        {{ $t('messages.admin.market_overviews.generation.error', { error: run.error }) }}
      </Message>

      <dl v-if="run.summary" class="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="mo-generation-summary">
        <div v-for="stat in STATS" :key="stat" class="rounded-lg bg-gray-50 px-3 py-2">
          <dt class="text-xs text-gray-500">{{ $t(`messages.admin.market_overviews.generation.stats.${stat}`) }}</dt>
          <dd class="text-lg font-medium text-gray-800" :data-testid="`mo-stat-${stat}`">{{ run.summary[stat] ?? 0 }}</dd>
        </div>
      </dl>

      <ul v-if="universal.length" class="mt-4 divide-y divide-gray-100 rounded-lg border border-gray-200" data-testid="mo-universal-list">
        <li
          v-for="row in universal"
          :key="row.family"
          class="flex flex-col gap-1 px-4 py-3"
          :data-testid="`mo-universal-${row.family}`"
        >
          <div class="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span class="text-sm font-medium text-gray-800">{{ row.description ?? row.family_label }}</span>
            <Tag :severity="universalTag(row).severity" :value="universalTag(row).label" class="text-xs!" />
            <span v-if="row.base_file" class="text-xs text-gray-500">
              {{ $t('messages.admin.market_overviews.generation.universal.base', { file: row.base_file }) }}
            </span>
          </div>
          <ul v-if="row.reasons.length" class="list-disc pl-5 text-xs text-gray-600">
            <li v-for="reason in row.reasons" :key="reason" class="break-words">{{ reason }}</li>
          </ul>
        </li>
      </ul>

      <Message
        v-for="candidate in candidates"
        :key="candidate.template_id"
        severity="info"
        size="small"
        class="mt-3"
      >
        {{
          $t('messages.admin.market_overviews.generation.candidate', {
            file: candidate.file,
            title: candidate.title,
            published: candidate.published_instead,
          })
        }}
      </Message>

      <div class="mt-4">
        <Button
          v-if="malls.length"
          type="button"
          text
          size="small"
          :icon="mallsOpen ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
          :label="
            mallsOpen
              ? $t('messages.admin.market_overviews.generation.malls.toggle_hide')
              : $t('messages.admin.market_overviews.generation.malls.toggle_show', { n: malls.length })
          "
          data-testid="mo-malls-toggle"
          @click="mallsOpen = !mallsOpen"
        />
        <p v-else class="text-sm text-gray-500">{{ $t('messages.admin.market_overviews.generation.malls.none') }}</p>

        <DataTable
          v-if="mallsOpen && malls.length"
          :value="malls"
          size="small"
          striped-rows
          data-key="template_id"
          class="mt-2 text-sm"
          data-testid="mo-malls-table"
        >
          <Column :header="$t('messages.admin.market_overviews.generation.malls.mall')">
            <template #body="{ data }">
              <span class="block font-medium text-gray-800">{{ data.description ?? data.file }}</span>
              <span class="block text-xs text-gray-500">{{ data.file }}</span>
            </template>
          </Column>
          <Column :header="$t('messages.admin.market_overviews.generation.malls.overview')">
            <template #body="{ data }">
              <span :class="data.status === 'regional' ? 'text-gray-800' : 'text-gray-500'">{{ cellLabel(data) }}</span>
            </template>
          </Column>
          <Column
            v-for="gate in ['gate_a', 'gate_b', 'gate_c']"
            :key="gate"
            :header="$t(`messages.admin.market_overviews.generation.malls.${gate}`)"
          >
            <template #body="{ data }">
              <Tag
                v-if="data[gate]"
                :severity="gateSeverity(data[gate])"
                :value="$t(`messages.admin.market_overviews.generation.gate.${data[gate]}`)"
                class="text-xs!"
                :data-testid="`mo-${gate}-${data.template_id}`"
              />
              <span v-else class="text-gray-400">—</span>
            </template>
          </Column>
          <Column :header="$t('messages.admin.market_overviews.generation.malls.notes')">
            <template #body="{ data }">
              <span v-if="data.reason" class="text-xs text-gray-600">{{ data.reason }}</span>
              <ul v-else-if="data.problems.length" class="list-disc pl-4 text-xs text-gray-600" :data-testid="`mo-problems-${data.template_id}`">
                <li v-for="problem in data.problems" :key="problem" class="break-words">{{ problem }}</li>
              </ul>
            </template>
          </Column>
        </DataTable>
      </div>
    </template>

    <div class="mt-5 flex flex-wrap items-center justify-end gap-3 border-t border-gray-100 pt-4">
      <span class="text-sm text-red-600" :class="{ invisible: !error }" data-testid="mo-generate-error">{{ error ?? '·' }}</span>
      <Button
        type="button"
        :label="buttonLabel"
        :icon="busy ? 'pi pi-spin pi-spinner' : 'pi pi-refresh'"
        :disabled="busy"
        data-testid="mo-generate"
        @click="generate"
      />
    </div>
  </section>
</template>
